import React from "react";
import { Link, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import AmbientBackground from "./AmbientBackground";
import AiOperationsVisual from "./AiOperationsVisual";
import {
  AnalyticsDashboardVisual,
  AnalyticsFocusVisual,
  AnalyticsMethodVisual,
  AnalyticsProofVisual,
  analyticsFocusVisuals,
  analyticsMethodVisuals,
} from "./AnalyticsVisuals";
import DataPlatformHeroVisual, {
  DataPlatformFocusVisual,
  DataPlatformMethodVisual,
  DataPlatformProofVisual,
  dataPlatformFocusVisuals,
  dataPlatformMethodVisuals,
} from "./DataPlatformHeroVisual";
import {
  HERO_COPY_TYPE_DELAY_MS,
  HeroTypingLead,
  HeroTypingTitle,
} from "./HeroTypingIntro";
import LeadMagnetSection from "./LeadMagnetSection";
import MarketingMediaSlot from "./MarketingMediaSlot";
import { trackEvent } from "../lib/analytics";
import {
  AnimatedSection,
  ContentPageInner,
  ContentPageShell,
  GlassPanel,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SectionIntro,
  SectionKicker,
  SectionLead,
} from "../styles/contentPage";

const intakeRows = [20, 52, 84];
const intakePaths = [
  "M98 31C129 31 139 50 158 59C165 62 171 63 176 63H224",
  "M98 63H224",
  "M98 95C129 95 139 76 158 67C165 64 171 63 176 63H224",
];
const controlPaths = [
  "M76 31C116 31 118 52 154 58",
  "M76 63H154",
  "M76 95C116 95 118 74 154 66",
];
const controlFlowDelays = ["", " delay-a", " delay-b"];
const intakeTaskRows = [
  { y: 13, width: 34, strong: true },
  { y: 28, width: 42 },
  { y: 43, width: 36 },
  { y: 58, width: 28, strong: true },
];
const agentOutputRows = [
  { y: 14, width: 28, strong: true },
  { y: 29, width: 36 },
  { y: 44, width: 25 },
];
const aiMethodVisuals = ["analyze", "choose", "build", "expand"];
const methodChoiceCandidates = [
  { y: 15, first: 30, second: 22 },
  { y: 38, first: 50, second: 38, selected: true },
  { y: 61, first: 34, second: 26 },
  { y: 84, first: 40, second: 18 },
];
const methodSearchOrbitPath = "M0 0C-6 -8 -23 -7 -25 5C-27 17 -9 19 0 0";
const methodBuildSteps = [
  { key: "intake", y: 12, width: 72, icon: "M3 4H13V11H3ZM3 5L8 8.5L13 5" },
  { key: "draft", y: 34, width: 86, icon: "M4 2H10L13 5V13H4ZM10 2V5H13M6 8H11M6 11H10" },
  { key: "review", y: 56, width: 66, icon: "M8 2L13 4V8C13 11 10 13 8 14C6 13 3 11 3 8V4ZM6 8L7.5 9.5L11 6" },
  { key: "followup", y: 78, width: 78, icon: "M3 8H12M9 5L12 8L9 11" },
];
const methodAutomationGearTeeth = [0, 45, 90, 135, 180, 225, 270, 315];

export default function ExpertisePageTemplate({
  pageKey,
  data,
  mediaSlotLabel,
  primaryHref = "/contact",
}) {
  const navigate = useNavigate();

  const openTarget = (target, label, location = "unknown") => {
    if (!target) return;

    trackEvent("cta_strategy_call_click", {
      source: `${pageKey}-${location}`,
      destination: target.externalUrl || target.href || "",
      label,
      external: Boolean(target.externalUrl),
    });

    if (target.externalUrl) {
      window.open(target.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(target.href);
  };

  const heroLead = Array.isArray(data.hero?.lead) ? data.hero.lead : [];
  const focusItems = Array.isArray(data.focus?.items) ? data.focus.items : [];
  const methodSteps = Array.isArray(data.method?.steps) ? data.method.steps : [];
  const supportingItems = Array.isArray(data.proof?.supporting?.items)
    ? data.proof.supporting.items
    : [];
  const featuredTarget = data.proof?.featured;
  const nexBridge = data.nexBridge || {};
  const nexBridgeChips = Array.isArray(nexBridge.visualChips)
    ? nexBridge.visualChips
    : ["IAM", "RAG", "Analytics", "API"];
  const hasFeaturedTarget = Boolean(featuredTarget?.externalUrl || featuredTarget?.href);
  const heroMeta = Array.isArray(data.hero?.meta) ? data.hero.meta : [];
  const aiFocusVisuals = ["intake", "agent", "control"];
  const leadMagnetAfterProof = pageKey === "integrate-ai";
  const HeroVisual =
    pageKey === "integrate-ai"
      ? AiOperationsVisual
      : pageKey === "advanced-analytics"
        ? AnalyticsDashboardVisual
        : pageKey === "data-platforms"
          ? DataPlatformHeroVisual
          : undefined;
  const hasCustomHeroVisual = Boolean(HeroVisual);

  return (
    <Wrap className="page-container">
      <AmbientBackground />
      <Inner>
        <AnimatedSection $i={0}>
          <HeroGrid>
            <HeroColumn>
              <TypingHeroTitle data-expertise-hero-title-typing>
                {data.hero?.title}
              </TypingHeroTitle>
              <HeroRichLead
                data-expertise-hero-copy-typing
                text={heroLead}
                separator={"\n\n"}
                initialDelayMs={HERO_COPY_TYPE_DELAY_MS}
              />
              <HeroActions>
                <PrimaryButton
                  onClick={() =>
                    openTarget(
                      { href: primaryHref },
                      data.hero?.primaryCta,
                      "hero-primary"
                    )
                  }
                >
                  {data.hero?.primaryCta}
                </PrimaryButton>
                {data.hero?.secondaryCta ? (
                  <SecondaryButton
                    onClick={() =>
                      openTarget(data.hero?.secondaryTarget, data.hero?.secondaryCta, "hero-secondary")
                    }
                  >
                    {data.hero?.secondaryCta}
                  </SecondaryButton>
                ) : null}
              </HeroActions>
            </HeroColumn>

            <HeroVisualPanel>
              <HeroMedia
                as={HeroVisual}
                src={hasCustomHeroVisual ? undefined : data.hero?.media?.image}
                alt={hasCustomHeroVisual ? undefined : data.hero?.media?.alt}
                title={hasCustomHeroVisual ? data.hero?.media?.alt : undefined}
                label={mediaSlotLabel}
                hint={data.hero?.media?.placeholder}
                aspect="16 / 14"
              />
              {heroMeta.length ? (
                <HeroMetaRail>
                  {heroMeta.map((item) => (
                    <MetaItem key={item.label}>
                      <MetaLabel>{item.label}</MetaLabel>
                      <MetaValue>{item.value}</MetaValue>
                    </MetaItem>
                  ))}
                </HeroMetaRail>
              ) : null}
            </HeroVisualPanel>
          </HeroGrid>
        </AnimatedSection>

        <AnimatedSection $i={1}>
          <SectionIntro>
            <SectionKicker>{data.focus?.title}</SectionKicker>
            <SectionHeading>{data.focus?.heading}</SectionHeading>
            <SectionLead>{data.focus?.lead}</SectionLead>
          </SectionIntro>

          <FocusGrid>
            {focusItems.map((item, index) => (
              <FocusCard key={item.title}>
                {pageKey === "integrate-ai" ? (
                  <AiFocusVisual variant={aiFocusVisuals[index]} />
                ) : null}
                {pageKey === "advanced-analytics" ? (
                  <AnalyticsFocusVisual variant={analyticsFocusVisuals[index]} />
                ) : null}
                {pageKey === "data-platforms" ? (
                  <DataPlatformFocusVisual variant={dataPlatformFocusVisuals[index]} />
                ) : null}
                <FocusNumber>{String(index + 1).padStart(2, "0")}</FocusNumber>
                <FocusTitle>{item.title}</FocusTitle>
                <FocusText>{item.description}</FocusText>
              </FocusCard>
            ))}
          </FocusGrid>
        </AnimatedSection>

        <AnimatedSection $i={2}>
          <SectionIntro>
            <SectionKicker>{data.method?.title}</SectionKicker>
            <SectionHeading>{data.method?.heading}</SectionHeading>
            <SectionLead>{data.method?.lead}</SectionLead>
          </SectionIntro>

          <MethodGrid>
            {methodSteps.map((step, index) => (
              <MethodCard key={step.title}>
                {pageKey === "integrate-ai" ? (
                  <AiMethodVisual variant={aiMethodVisuals[index]} />
                ) : null}
                {pageKey === "advanced-analytics" ? (
                  <AnalyticsMethodVisual variant={analyticsMethodVisuals[index]} />
                ) : null}
                {pageKey === "data-platforms" ? (
                  <DataPlatformMethodVisual variant={dataPlatformMethodVisuals[index]} />
                ) : null}
                <MethodIndex>{String(index + 1).padStart(2, "0")}</MethodIndex>
                <MethodTitle>{step.title}</MethodTitle>
                <MethodText>{step.description}</MethodText>
              </MethodCard>
            ))}
          </MethodGrid>
        </AnimatedSection>

        {!leadMagnetAfterProof ? (
          <AnimatedSection $i={3}>
            <LeadMagnetSection data={data.leadMagnet} pageKey={pageKey} />
          </AnimatedSection>
        ) : null}

        <AnimatedSection $i={leadMagnetAfterProof ? 3 : 4}>
          <SectionIntro>
            <SectionKicker>{data.proof?.title}</SectionKicker>
            <SectionHeading>{data.proof?.heading}</SectionHeading>
            <SectionLead>{data.proof?.lead}</SectionLead>
          </SectionIntro>

          <ProofGrid>
            <FeaturedProof
              as={hasFeaturedTarget ? "button" : "article"}
              type={hasFeaturedTarget ? "button" : undefined}
              onClick={
                hasFeaturedTarget
                  ? () =>
                      openTarget(
                        data.proof?.featured,
                        data.proof?.featured?.title,
                        "proof-featured"
                      )
                  : undefined
              }
              $interactive={hasFeaturedTarget}
            >
              <FeaturedMedia
                as={
                  pageKey === "advanced-analytics"
                    ? AnalyticsProofVisual
                    : pageKey === "data-platforms"
                      ? DataPlatformProofVisual
                      : undefined
                }
                src={
                  pageKey === "advanced-analytics" || pageKey === "data-platforms"
                    ? undefined
                    : data.proof?.featured?.image
                }
                alt={
                  pageKey === "advanced-analytics" || pageKey === "data-platforms"
                    ? undefined
                    : data.proof?.featured?.alt
                }
                title={
                  pageKey === "advanced-analytics" || pageKey === "data-platforms"
                    ? data.proof?.featured?.alt
                    : undefined
                }
                label={mediaSlotLabel}
                hint={data.proof?.featured?.placeholder}
                aspect="16 / 10"
              />
              <FeaturedMeta>
                <FeaturedLabel>{data.proof?.featured?.label}</FeaturedLabel>
                    {data.proof?.featured?.externalUrl ? (
                      <FeaturedTag>{data.proof?.externalLabel || "Extern"}</FeaturedTag>
                    ) : null}
              </FeaturedMeta>
              <FeaturedTitle>{data.proof?.featured?.title}</FeaturedTitle>
              <FeaturedText>{data.proof?.featured?.description}</FeaturedText>
              {data.proof?.featured?.cta ? <FeaturedLink>{data.proof.featured.cta} →</FeaturedLink> : null}
            </FeaturedProof>

            <SupportingColumn>
              {supportingItems.map((item) => (
                <SupportingItem key={item.title}>
                  <SupportingTitle>{item.title}</SupportingTitle>
                  <SupportingText>{item.description}</SupportingText>
                </SupportingItem>
              ))}
            </SupportingColumn>
          </ProofGrid>
        </AnimatedSection>

        {leadMagnetAfterProof ? (
          <AnimatedSection $i={4}>
            <LeadMagnetSection data={data.leadMagnet} pageKey={pageKey} />
          </AnimatedSection>
        ) : null}

        {nexBridge.title ? (
          <AnimatedSection $i={5}>
            <NexBridgePanel>
              <NexBridgeCopy>
                <SectionKicker>{nexBridge.kicker}</SectionKicker>
                <NexBridgeTitle>{nexBridge.title}</NexBridgeTitle>
                <NexBridgeText>{nexBridge.body}</NexBridgeText>
                <NexBridgeButton
                  as={Link}
                  to={nexBridge.href || "/nex"}
                  onClick={() =>
                    trackEvent("cta_strategy_call_click", {
                      source: `${pageKey}-nex-bridge`,
                      destination: nexBridge.href || "/nex",
                      label: nexBridge.cta,
                      external: false,
                    })
                  }
                >
                  {nexBridge.cta} →
                </NexBridgeButton>
              </NexBridgeCopy>
              <NexBridgeSystem aria-hidden="true">
                <NexBridgeLogoStage>
                  <span className="nex-logo-glow nex-logo-glow-a" />
                  <span className="nex-logo-glow nex-logo-glow-b" />
                  <NexBridgeLogo src={nexBridge.logoSrc || "/NEX.png"} alt="" />
                </NexBridgeLogoStage>
                <NexBridgeChipGrid>
                  {nexBridgeChips.map((chip) => (
                    <span key={chip}>{chip}</span>
                  ))}
                </NexBridgeChipGrid>
              </NexBridgeSystem>
            </NexBridgePanel>
          </AnimatedSection>
        ) : null}

        <AnimatedSection $i={6}>
          <CtaPanel>
            <CtaCopy>
              {data.cta?.title ? <SectionKicker>{data.cta.title}</SectionKicker> : null}
              <CtaHeading>{data.cta?.heading}</CtaHeading>
              <CtaText>{data.cta?.text}</CtaText>
            </CtaCopy>
            <CtaActions>
              <PrimaryButton
                onClick={() =>
                  openTarget({ href: primaryHref }, data.cta?.primary, "final-primary")
                }
              >
                {data.cta?.primary}
              </PrimaryButton>
              {data.cta?.secondary ? (
                <SecondaryButton
                  onClick={() =>
                    openTarget(data.cta?.secondaryTarget, data.cta?.secondary, "final-secondary")
                  }
                >
                  {data.cta?.secondary}
                </SecondaryButton>
              ) : null}
            </CtaActions>
          </CtaPanel>
        </AnimatedSection>
      </Inner>
    </Wrap>
  );
}

function AiFocusVisual({ variant }) {
  return (
    <FocusVisualFrame data-ai-focus-visual={variant} aria-hidden="true">
      <svg viewBox="0 0 360 126" focusable="false">
        <rect className="focus-visual-bg" x="1" y="1" width="358" height="124" rx="24" />

        {variant === "intake" ? (
          <>
            {intakeRows.map((rowY) => (
              <g key={rowY} transform={`translate(22 ${rowY})`}>
                <rect className="focus-chip" width="76" height="22" rx="8" />
                <rect className="focus-icon-dot" x="10" y="7" width="8" height="8" rx="2" />
                <path className="focus-line muted" d="M28 8H60M28 14H48" />
              </g>
            ))}
            {intakePaths.map((path) => (
              <path key={`track-${path}`} data-ai-focus-intake-track className="focus-track" d={path} />
            ))}
            {intakePaths.map((path) => (
              <path
                key={`flow-${path}`}
                data-ai-focus-flow
                data-ai-focus-intake-flow
                className="focus-flow intake-flow"
                d={path}
              />
            ))}
            <g data-ai-focus-route-arrow className="route-arrow-hit">
              <path className="focus-route-node" d="M208 47L224 63L208 79" />
            </g>
            <g transform="translate(246 24)">
              <rect data-ai-focus-task-output className="focus-result" width="90" height="78" rx="17" />
              <g data-ai-focus-task-stack>
                {intakeTaskRows.map((row, index) => (
                  <g key={row.y} transform={`translate(14 ${row.y})`}>
                    <g
                      data-ai-focus-task-row
                      className={`task-stack-row task-stack-row-${index + 1}`}
                    >
                      <rect className="task-box" width="8" height="8" rx="2" />
                      <path
                        className={row.strong ? "focus-line strong" : "focus-line"}
                        d={`M18 4H${18 + row.width}`}
                      />
                    </g>
                  </g>
                ))}
              </g>
            </g>
          </>
        ) : null}

        {variant === "agent" ? (
          <>
            <g data-ai-focus-agent-input-card transform="translate(34 38)">
              <rect className="agent-side-card" width="66" height="50" rx="15" />
              <rect className="task-box" x="13" y="13" width="8" height="8" rx="2" />
              <path className="focus-line strong" d="M28 17H52" />
              <path className="focus-line" d="M13 31H45" />
              <path className="focus-line muted" d="M13 40H36" />
            </g>
            <path className="focus-track" d="M100 63H132" />
            <path data-ai-focus-flow className="focus-flow" d="M100 63H132" />
            <rect className="agent-orbit-track" x="130" y="25" width="100" height="76" rx="26" />
            <rect data-ai-focus-agent-orbit className="agent-orbit" x="130" y="25" width="100" height="76" rx="26" />
            <g data-ai-focus-agent-core transform="translate(142 34)">
              <rect className="agent-core" width="76" height="58" rx="18" />
              <text className="agent-ai-label" x="38" y="36" textAnchor="middle">AI</text>
              <path className="agent-core-line" d="M18 45H58" />
              <path data-ai-focus-agent-scan className="agent-scan" d="M17 45H59" />
            </g>
            <path className="focus-track" d="M228 63H272" />
            <path data-ai-focus-flow className="focus-flow delay-a" d="M228 63H272" />
            <g data-ai-focus-agent-output-card transform="translate(272 38)">
              <rect className="agent-side-card" width="64" height="50" rx="15" />
              {agentOutputRows.map((row) => (
                <path
                  key={row.y}
                  className={row.strong ? "focus-line strong" : "focus-line"}
                  d={`M12 ${row.y}H${12 + row.width}`}
                />
              ))}
              <path
                data-ai-focus-agent-output-check
                className="focus-check small agent-output-check"
                d="M42 32L48 38L58 20"
              />
            </g>
          </>
        ) : null}

        {variant === "control" ? (
          <>
            {[0, 1, 2].map((item) => (
              <g key={item} transform={`translate(30 ${18 + item * 32})`}>
                <rect className="tool-node" width="46" height="26" rx="9" />
                <path className="focus-line muted" d="M13 11H32M13 17H27" />
              </g>
            ))}
            <g transform="translate(154 32)">
              <path className="review-shield-mini" d="M26 0L50 9V28C50 46 37 56 26 61C15 56 2 46 2 28V9Z" />
              <path className="shield-mini-check" d="M17 31L24 38L38 20" />
            </g>
            <g transform="translate(262 32)">
              <rect className="focus-result" width="66" height="62" rx="16" />
              <circle className="human-mini" cx="20" cy="24" r="8" />
              <path className="human-mini-line" d="M8 46C12 36 28 36 32 46" />
              <path className="focus-line" d="M42 23H56M42 35H54" />
            </g>
            {controlPaths.map((path) => (
              <path
                key={`control-track-${path}`}
                data-ai-focus-control-track
                className="focus-track"
                d={path}
              />
            ))}
            <path className="focus-track" d="M262 63H206" />
            {controlPaths.map((path, index) => (
              <path
                key={`control-flow-${path}`}
                data-ai-focus-flow
                data-ai-focus-control-flow
                className={`focus-flow control-flow${controlFlowDelays[index]}`}
                d={path}
              />
            ))}
            <path
              data-ai-focus-review-return
              className="focus-flow review-return-flow delay-c"
              d="M262 63H206"
            />
          </>
        ) : null}
      </svg>
    </FocusVisualFrame>
  );
}

function AiMethodVisual({ variant }) {
  return (
    <MethodVisualFrame data-ai-method-visual={variant} aria-hidden="true">
      <svg viewBox="0 0 240 118" focusable="false">
        <rect className="method-bg" x="1" y="1" width="238" height="116" rx="22" />

        {variant === "analyze" ? (
          <>
            {[18, 48, 78].map((y) => (
              <g key={y} transform={`translate(18 ${y})`}>
                <rect className="method-chip" width="54" height="20" rx="8" />
                <rect className="method-square" x="9" y="6" width="8" height="8" rx="2" />
                <path className="method-line muted" d="M23 7H42M23 13H36" />
              </g>
            ))}
            <g data-ai-method-friction transform="translate(112 34)">
              <g data-ai-method-search-lens className="method-search-lens">
                <animateMotion
                  data-ai-method-search-motion
                  path={methodSearchOrbitPath}
                  dur="4.8s"
                  repeatCount="indefinite"
                />
                <circle className="method-friction-ring" cx="22" cy="24" r="19" />
                <circle data-ai-method-orbit className="method-orbit" cx="22" cy="24" r="21" />
                <path className="method-lens-glint" d="M14 17C18 13 25 12 31 15" />
                <path data-ai-method-lens-handle className="method-lens" d="M38 40L51 53" />
              </g>
            </g>
            <path data-ai-method-search-arrow className="method-search-arrow" d="M163 58H170M165 53L170 58L165 63" />
            <path data-ai-method-search-result-flow className="method-search-result-flow" d="M163 58H170" />
            <g transform="translate(174 39)">
              <rect className="method-node" width="44" height="38" rx="13" />
              <path className="method-line strong" d="M12 13H32" />
              <path className="method-line" d="M12 24H27" />
            </g>
          </>
        ) : null}

        {variant === "choose" ? (
          <>
            {methodChoiceCandidates.map((candidate) => (
              <g
                key={candidate.y}
                data-ai-method-choice-candidate={candidate.selected ? "selected" : "option"}
                data-ai-method-selected-route={candidate.selected ? true : undefined}
                transform={`translate(18 ${candidate.y})`}
                className={candidate.selected ? "method-choice-selected" : "method-choice-option"}
              >
                <rect className="method-candidate-card" width="82" height="18" rx="8" />
                <circle className="method-dot" cx="10" cy="9" r="4.5" />
                <path
                  className={candidate.selected ? "method-selected-path" : "method-line muted"}
                  d={`M22 7H${22 + candidate.first}`}
                />
                <path
                  className="method-line tiny"
                  d={`M22 13H${22 + candidate.second}`}
                />
              </g>
            ))}
            <path data-ai-method-selected-connector className="method-selected-connector" d="M100 47C110 47 112 53 116 56" />
            <g data-ai-method-first-flow transform="translate(120 26)">
              <rect className="method-flow-card" width="58" height="66" rx="18" />
              <g className="method-ai-core-mini" transform="translate(17 9)">
                <g data-ai-method-core-spin className="method-ai-core-spin">
                  <circle className="method-ai-core-ring" cx="12" cy="12" r="11" />
                  <circle className="method-ai-core-dot" cx="12" cy="12" r="3" />
                  <path className="method-ai-core-link" d="M12 12L5 17M12 12L19 17M12 12V4" />
                  <circle className="method-ai-node-dot" cx="5" cy="17" r="2.2" />
                  <circle className="method-ai-node-dot" cx="19" cy="17" r="2.2" />
                  <circle className="method-ai-node-dot" cx="12" cy="4" r="2.2" />
                </g>
              </g>
              <g data-ai-method-usable-gain transform="translate(13 42)">
                <rect className="method-gain-bar gain-a" x="0" y="10" width="6" height="9" rx="2" />
                <rect className="method-gain-bar gain-b" x="11" y="5" width="6" height="14" rx="2" />
                <rect className="method-gain-bar gain-c" x="22" y="0" width="6" height="19" rx="2" />
                <path className="method-gain-check" d="M34 10L39 15L49 3" />
              </g>
            </g>
            <path data-ai-method-control-link className="method-selected-connector" d="M178 58H195" />
            <g data-ai-method-control-guard transform="translate(194 37) scale(0.68)">
              <path
                data-ai-method-control-shield
                className="method-guard-shield"
                d="M26 0L50 9V28C50 46 37 56 26 61C15 56 2 46 2 28V9Z"
              />
              <path className="method-check guard-check method-guard-check" d="M17 31L24 38L38 20" />
            </g>
          </>
        ) : null}

        {variant === "build" ? (
          <>
            <g data-ai-method-prototype transform="translate(12 10)">
              <rect className="method-prototype-card method-v1-shell" width="154" height="98" rx="18" />
              <path data-ai-method-build-runner-track className="method-build-runner-track" d="M136 20V86" />
              <path data-ai-method-build-runner className="method-build-runner" d="M136 20V86" />
              {methodBuildSteps.map((step, index) => (
                <g
                  key={step.key}
                  data-ai-method-workflow-step={step.key}
                  className={`method-workflow-step method-workflow-step-${index + 1}`}
                  transform={`translate(12 ${step.y})`}
                >
                  <rect className="method-step-icon" width="16" height="16" rx="5" />
                  <path className="method-step-glyph" d={step.icon} />
                  <path className={index === 1 ? "method-line strong" : "method-line"} d={`M25 8H${25 + step.width}`} />
                  <circle className="method-step-status" cx="124" cy="8" r="2.6" />
                </g>
              ))}
            </g>
            <path className="method-track" d="M166 59H182" />
            <path data-ai-method-build-live-flow className="method-build-live-flow" d="M166 59H182" />
            <g data-ai-method-v1-badge transform="translate(192 12)">
              <rect className="method-v1-badge" width="22" height="12" rx="5" />
              <text className="method-v1-text" x="11" y="9" textAnchor="middle">V1</text>
            </g>
            <g data-ai-method-team-test transform="translate(182 35)">
              <rect className="method-test-card" width="42" height="48" rx="15" />
              <circle className="method-test-avatar" cx="16" cy="16" r="6" />
              <path className="method-test-avatar-line" d="M7 34C10 26 22 26 25 34" />
              <path className="method-line muted" d="M30 16H38M30 26H36" />
            </g>
          </>
        ) : null}

        {variant === "expand" ? (
          <>
            <g data-ai-method-expansion transform="translate(20 38)">
              <rect className="method-selected-card method-expansion-base" width="62" height="42" rx="14" />
              <path className="method-line strong" d="M13 14H39" />
              <path className="method-line" d="M13 25H33" />
              <path className="method-gain-check" d="M42 25L47 30L55 17" />
            </g>
            <path className="method-track" d="M82 59H104" />
            <g data-ai-method-expand-gate transform="translate(104 34)">
              <rect className="method-expand-gate" width="40" height="50" rx="15" />
              <path data-ai-method-expansion-activation className="method-plus method-expand-plus" d="M20 15V29M13 22H27" />
              <path className="method-line tiny" d="M13 36H27" />
            </g>
            <path className="method-track" d="M144 59C154 59 150 34 160 34" />
            <path className="method-track" d="M144 59C154 59 150 84 160 84" />
            <path data-ai-method-expansion-flow className="method-expansion-flow" d="M144 59C154 59 150 34 160 34" />
            <path data-ai-method-expansion-flow className="method-expansion-flow" d="M144 59C154 59 150 84 160 84" />
            <g data-ai-method-expansion-module="integration" className="method-expansion-module" transform="translate(160 16)">
              <rect className="method-mini-node method-expansion-node-large" width="64" height="36" rx="12" />
              <g data-ai-method-integration-hybrid transform="translate(13.5 4.5) scale(1.12)">
                <path
                  data-ai-method-integration-gear
                  className="method-integration-gear"
                  d="M13 2L10 4L7 3L5 6L6 9L3 11V15L6 17L5 20L7 22L10 21L13 23M13 5V20"
                />
                <path
                  data-ai-method-integration-circuit
                  className="method-integration-circuit"
                  d="M13 6H17.4L20 3.7H24M13 12.85H24M13 19.7H17.4L20 22H24"
                />
                <circle className="method-integration-pin" cx="24" cy="3.7" r="1.7" />
                <circle className="method-integration-pin" cx="24" cy="12.85" r="1.7" />
                <circle className="method-integration-pin" cx="24" cy="22" r="1.7" />
              </g>
            </g>
            <g data-ai-method-expansion-module="automation" className="method-expansion-module delay-a" transform="translate(160 66)">
              <rect className="method-mini-node method-expansion-node-large" width="64" height="36" rx="12" />
              <g data-ai-method-automation-gear className="method-automation-gear" transform="translate(32 18)">
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 0 0"
                  to="360 0 0"
                  dur="7.5s"
                  repeatCount="indefinite"
                  additive="sum"
                />
                {methodAutomationGearTeeth.map((angle) => (
                  <path
                    key={angle}
                    className="method-automation-tooth"
                    d="M-2.7 -14.8H2.7L4.2 -9.6H-4.2Z"
                    transform={`rotate(${angle})`}
                  />
                ))}
                <circle className="method-automation-gear-shape" cx="0" cy="0" r="10.2" />
                <circle className="method-automation-rim" cx="0" cy="0" r="7.1" />
                <circle className="method-automation-center" cx="0" cy="0" r="5" />
              </g>
            </g>
          </>
        ) : null}
      </svg>
    </MethodVisualFrame>
  );
}

const Wrap = styled(ContentPageShell)``;

const Inner = styled(ContentPageInner)``;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: clamp(28px, 4vw, 54px);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const HeroColumn = styled.div`
  display: grid;
  gap: clamp(18px, 2vw, 26px);
`;

const TypingHeroTitle = styled(HeroTypingTitle)`
  max-width: 100%;
`;

const HeroRichLead = styled(HeroTypingLead)`
  max-width: 44rem;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const HeroVisualPanel = styled(GlassPanel)`
  display: grid;
  gap: 16px;
  padding: clamp(18px, 2vw, 22px);
  border-radius: 30px;
  align-self: stretch;
`;

const HeroMedia = styled(MarketingMediaSlot)`
  border-radius: 24px;
`;

const HeroMetaRail = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  display: grid;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const MetaLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const MetaValue = styled.span`
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.88);
  white-space: pre-line;
`;

const focusFlow = keyframes`
  to {
    stroke-dashoffset: -112;
  }
`;

const focusTaskStack = keyframes`
  0%, 100% {
    opacity: 0.72;
    transform: translateY(0) scale(1);
  }

  18% {
    opacity: 0.86;
    transform: translateY(-3px) scale(0.98);
  }

  30%, 52% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  62% {
    opacity: 0.78;
    transform: translateY(0) scale(1);
  }
`;

const focusRouteHit = keyframes`
  0%, 36%, 100% {
    opacity: 0.74;
    stroke: rgba(205, 235, 243, 0.72);
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }

  44%, 58% {
    opacity: 1;
    stroke: rgba(230, 248, 252, 0.96);
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.45));
  }
`;

const agentOrbit = keyframes`
  to {
    stroke-dashoffset: -176;
  }
`;

const agentScan = keyframes`
  to {
    stroke-dashoffset: -72;
  }
`;

const agentCoreBreathe = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 4px rgba(123, 210, 236, 0.18));
  }

  48%, 62% {
    filter: drop-shadow(0 0 10px rgba(123, 210, 236, 0.42));
  }
`;

const methodPulse = keyframes`
  0%, 100% {
    opacity: 0.72;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }

  44%, 58% {
    opacity: 1;
    filter: drop-shadow(0 0 9px rgba(123, 210, 236, 0.34));
  }
`;

const methodScan = keyframes`
  to {
    stroke-dashoffset: -48;
  }
`;

const methodCoreTurn = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const methodOrbitLoop = keyframes`
  to {
    stroke-dashoffset: -132;
  }
`;

const methodResultFlow = keyframes`
  to {
    stroke-dashoffset: -34;
  }
`;

const nexLogoGlow = keyframes`
  0%, 100% {
    opacity: 0.42;
    transform: translate(-50%, -50%) scale(0.94);
  }

  48%, 58% {
    opacity: 0.78;
    transform: translate(-50%, -50%) scale(1.04);
  }
`;

const FocusVisualFrame = styled.div`
  width: 100%;
  aspect-ratio: 360 / 126;
  overflow: hidden;
  border-radius: 20px;
  margin-bottom: clamp(4px, 0.8vw, 8px);
  background:
    radial-gradient(circle at 50% 48%, rgba(123, 210, 236, 0.12), transparent 36%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.016));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .focus-visual-bg,
  .focus-chip,
  .focus-core-card,
  .focus-result,
  .focus-mini-card,
  .agent-side-card,
  .tool-node {
    fill: rgba(255, 255, 255, 0.038);
    stroke: rgba(255, 255, 255, 0.16);
    stroke-width: 1.2;
  }

  .focus-visual-bg {
    fill: rgba(255, 255, 255, 0.018);
    stroke: rgba(255, 255, 255, 0.12);
  }

  .focus-track,
  .focus-flow,
  .focus-line,
  .focus-route-node,
  .focus-check,
  .human-mini-line,
  .shield-mini-check {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .focus-track {
    stroke: rgba(255, 255, 255, 0.16);
    stroke-width: 2.1;
  }

  .focus-flow {
    stroke: rgba(205, 235, 243, 0.88);
    stroke-width: 2.8;
    stroke-dasharray: 28 84;
    animation: ${focusFlow} 3.2s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.24));
  }

  .delay-a {
    animation-delay: 0.32s;
  }

  .delay-b {
    animation-delay: 0.64s;
  }

  .delay-c {
    animation-delay: 0.96s;
  }

  .focus-line {
    stroke: rgba(170, 195, 202, 0.42);
    stroke-width: 3.2;
  }

  .focus-line.strong {
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 4.4;
  }

  .focus-line.muted {
    stroke-width: 2.4;
  }

  .focus-icon-dot,
  .human-mini {
    fill: rgba(205, 235, 243, 0.84);
  }

  .focus-check,
  .shield-mini-check {
    stroke: rgba(225, 235, 238, 0.9);
    stroke-width: 4;
  }

  .focus-check.small {
    stroke-width: 3.2;
  }

  .task-stack-row {
    opacity: 0.72;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${focusTaskStack} 4.2s ease-in-out infinite;
  }

  .task-stack-row .focus-line {
    stroke: rgba(170, 195, 202, 0.62);
  }

  .task-stack-row .focus-line.strong {
    stroke: rgba(205, 235, 243, 0.86);
  }

  .task-stack-row-1 {
    animation-delay: 0s;
  }

  .task-stack-row-2 {
    animation-delay: 0.2s;
  }

  .task-stack-row-3 {
    animation-delay: 0.4s;
  }

  .task-stack-row-4 {
    animation-delay: 0.6s;
  }

  .focus-route-node {
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 3;
  }

  .route-arrow-hit .focus-route-node {
    animation: ${focusRouteHit} 3.2s linear infinite;
  }

  .task-box {
    fill: rgba(205, 235, 243, 0.68);
  }

  .agent-core {
    fill: rgba(123, 210, 236, 0.1);
    stroke: rgba(205, 235, 243, 0.22);
    stroke-width: 1.4;
    animation: ${agentCoreBreathe} 3.2s ease-in-out infinite;
  }

  .agent-orbit {
    fill: none;
    stroke: rgba(123, 210, 236, 0.44);
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-dasharray: 36 140;
    animation: ${agentOrbit} 3.2s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.22));
  }

  .agent-orbit-track {
    fill: none;
    stroke: rgba(123, 210, 236, 0.16);
    stroke-width: 1.2;
  }

  .agent-ai-label {
    fill: rgba(205, 235, 243, 0.88);
    font-size: 27px;
    font-weight: 800;
    letter-spacing: 0;
  }

  .agent-core-line {
    fill: none;
    stroke: rgba(205, 235, 243, 0.46);
    stroke-width: 3;
    stroke-linecap: round;
  }

  .agent-scan {
    fill: none;
    stroke: rgba(230, 248, 252, 0.94);
    stroke-width: 3.4;
    stroke-linecap: round;
    stroke-dasharray: 12 60;
    animation: ${agentScan} 2.6s linear infinite;
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.35));
  }

  .agent-output-check {
    filter: drop-shadow(0 0 5px rgba(225, 235, 238, 0.2));
  }

  .review-shield-mini {
    fill: rgba(225, 235, 238, 0.84);
  }

  .shield-mini-check {
    stroke: rgba(8, 12, 14, 0.82);
    stroke-width: 4;
  }

  .human-mini {
    fill: rgba(123, 210, 236, 0.35);
    stroke: rgba(123, 210, 236, 0.8);
    stroke-width: 1.4;
  }

  .human-mini-line {
    stroke: rgba(123, 210, 236, 0.8);
    stroke-width: 1.5;
  }

  @media (prefers-reduced-motion: reduce) {
    .focus-flow,
    .task-stack-row,
    .route-arrow-hit .focus-route-node,
    .agent-orbit,
    .agent-scan,
    .agent-core {
      animation: none !important;
      opacity: 1;
    }
  }
`;

const FocusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const FocusCard = styled(GlassPanel)`
  display: grid;
  gap: 12px;
  padding: clamp(22px, 3vw, 28px);
  border-radius: 28px;
`;

const FocusNumber = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.48);
`;

const FocusTitle = styled.h3`
  margin: 0;
  font-size: clamp(26px, 2.8vw, 34px);
  line-height: 1.08;
`;

const FocusText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.74;
  color: rgba(255, 255, 255, 0.8);
`;

const MethodVisualFrame = styled.div`
  width: 100%;
  aspect-ratio: 240 / 118;
  overflow: hidden;
  border-radius: 20px;
  margin-bottom: clamp(6px, 1vw, 10px);
  background:
    radial-gradient(circle at 50% 50%, rgba(123, 210, 236, 0.1), transparent 42%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.046), rgba(255, 255, 255, 0.014));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .method-bg,
  .method-chip,
  .method-candidate-card,
  .method-flow-card,
  .method-node,
  .method-mini-node,
  .method-prototype-card,
  .method-selected-card,
  .method-step-icon,
  .method-test-card,
  .method-v1-badge,
  .method-expand-gate {
    fill: rgba(255, 255, 255, 0.035);
    stroke: rgba(255, 255, 255, 0.15);
    stroke-width: 1.2;
  }

  .method-bg {
    fill: rgba(255, 255, 255, 0.016);
    stroke: rgba(255, 255, 255, 0.11);
  }

  .method-selected-card,
  .method-prototype-card {
    fill: rgba(123, 210, 236, 0.07);
    stroke: rgba(205, 235, 243, 0.24);
  }

  .method-v1-shell {
    fill: rgba(12, 23, 26, 0.34);
  }

  .method-v1-badge {
    fill: rgba(123, 210, 236, 0.14);
    stroke: rgba(205, 235, 243, 0.26);
  }

  .method-v1-text {
    fill: rgba(225, 245, 250, 0.84);
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.06em;
  }

  .method-flow-card {
    fill: rgba(123, 210, 236, 0.055);
    stroke: rgba(205, 235, 243, 0.22);
    animation: ${methodPulse} 3.35s ease-in-out infinite;
  }

  .method-track,
  .method-line,
  .method-check,
  .method-arrow,
  .method-ai-core-link,
  .method-ai-core-ring,
  .method-gain-check,
  .method-lens,
  .method-lens-glint,
  .method-orbit,
  .method-search-arrow,
  .method-search-result-flow,
  .method-build-live-flow,
  .method-build-runner-track,
  .method-build-runner,
  .method-expansion-flow,
  .method-expansion-icon,
  .method-integration-gear,
  .method-integration-circuit,
  .method-inbox,
  .method-build-bar,
  .method-plus,
  .method-selected-path,
  .method-selected-connector,
  .method-step-glyph,
  .method-test-avatar-line,
  .method-test-cursor {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .method-track {
    stroke: rgba(255, 255, 255, 0.15);
    stroke-width: 2;
  }

  .delay-a {
    animation-delay: 0.36s;
  }

  .delay-b {
    animation-delay: 0.72s;
  }

  .method-line {
    stroke: rgba(170, 195, 202, 0.5);
    stroke-width: 3;
  }

  .method-line.strong {
    stroke: rgba(205, 235, 243, 0.78);
    stroke-width: 4.2;
  }

  .method-line.muted {
    stroke-width: 2.4;
  }

  .method-line.tiny {
    stroke: rgba(170, 195, 202, 0.32);
    stroke-width: 2.2;
  }

  .method-square,
  .method-dot,
  .method-step-status,
  .method-test-avatar {
    fill: rgba(205, 235, 243, 0.74);
  }

  .method-choice-selected .method-dot,
  .method-plus {
    stroke: rgba(205, 235, 243, 0.88);
  }

  .method-choice-selected .method-candidate-card {
    fill: rgba(123, 210, 236, 0.075);
    stroke: rgba(205, 235, 243, 0.24);
    animation: ${methodPulse} 3.15s ease-in-out infinite;
  }

  .method-choice-selected .method-dot {
    fill: rgba(205, 235, 243, 0.92);
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.28));
  }

  .method-selected-path {
    stroke: rgba(205, 235, 243, 0.86);
    stroke-width: 4;
    animation: ${methodPulse} 3.1s ease-in-out infinite;
  }

  .method-selected-connector {
    stroke: rgba(205, 235, 243, 0.36);
    stroke-width: 2.2;
  }

  .method-ai-core-spin {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${methodCoreTurn} 6.2s linear infinite;
  }

  .method-ai-core-ring {
    stroke: rgba(205, 235, 243, 0.36);
    stroke-width: 1.4;
  }

  .method-ai-core-link {
    stroke: rgba(205, 235, 243, 0.34);
    stroke-width: 1.2;
  }

  .method-ai-core-dot,
  .method-ai-node-dot {
    fill: rgba(205, 235, 243, 0.86);
  }

  .method-gain-bar {
    fill: rgba(123, 210, 236, 0.55);
    animation: ${methodPulse} 3.25s ease-in-out infinite;
  }

  .gain-b {
    animation-delay: 0.2s;
  }

  .gain-c {
    animation-delay: 0.4s;
  }

  .method-gain-check {
    stroke: rgba(225, 235, 238, 0.88);
    stroke-width: 2.8;
  }

  .method-guard-shield {
    fill: rgba(225, 235, 238, 0.8);
    animation: ${methodPulse} 3.4s ease-in-out infinite;
    filter: drop-shadow(0 0 6px rgba(225, 235, 238, 0.14));
  }

  .method-friction-ring {
    fill: rgba(12, 23, 26, 0.94);
    stroke: rgba(205, 235, 243, 0.26);
    stroke-width: 1.6;
  }

  .method-orbit {
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 1.8;
    stroke-dasharray: 24 108;
    animation: ${methodOrbitLoop} 3.6s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.2));
  }

  .method-search-arrow {
    stroke: rgba(205, 235, 243, 0.5);
    stroke-width: 2.2;
  }

  .method-search-result-flow {
    stroke: rgba(225, 245, 250, 0.9);
    stroke-width: 2.6;
    stroke-dasharray: 7 27;
    animation: ${methodResultFlow} 1.9s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.28));
  }

  .method-build-live-flow {
    stroke: rgba(225, 245, 250, 0.88);
    stroke-width: 2.7;
    stroke-dasharray: 8 24;
    animation: ${methodResultFlow} 1.9s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.26));
  }

  .method-build-runner-track {
    stroke: rgba(205, 235, 243, 0.28);
    stroke-width: 2;
  }

  .method-build-runner {
    stroke: rgba(225, 245, 250, 0.82);
    stroke-width: 2.4;
    stroke-dasharray: 9 36;
    animation: ${methodScan} 2.9s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.22));
  }

  .method-workflow-step {
    animation: ${methodPulse} 3.8s ease-in-out infinite;
  }

  .method-workflow-step-2 {
    animation-delay: 0.22s;
  }

  .method-workflow-step-3 {
    animation-delay: 0.44s;
  }

  .method-workflow-step-4 {
    animation-delay: 0.66s;
  }

  .method-step-icon {
    fill: rgba(123, 210, 236, 0.08);
    stroke: rgba(205, 235, 243, 0.18);
  }

  .method-step-glyph {
    stroke: rgba(205, 235, 243, 0.76);
    stroke-width: 1.3;
  }

  .method-step-status {
    opacity: 0.78;
    filter: drop-shadow(0 0 4px rgba(123, 210, 236, 0.22));
  }

  .method-lens-glint {
    stroke: rgba(205, 235, 243, 0.26);
    stroke-width: 1.5;
  }

  .method-lens {
    stroke: rgba(205, 235, 243, 0.46);
    stroke-width: 3;
  }

  .method-check,
  .method-arrow,
  .method-inbox,
  .method-plus {
    stroke: rgba(225, 235, 238, 0.86);
    stroke-width: 3.2;
  }

  .method-test-card {
    fill: rgba(255, 255, 255, 0.028);
    stroke: rgba(255, 255, 255, 0.15);
  }

  .method-test-avatar {
    opacity: 0.78;
  }

  .method-test-avatar-line {
    stroke: rgba(205, 235, 243, 0.58);
    stroke-width: 1.8;
  }

  .method-test-cursor {
    fill: rgba(205, 235, 243, 0.08);
    stroke: rgba(225, 245, 250, 0.84);
    stroke-width: 1.5;
    animation: ${methodPulse} 3.1s ease-in-out infinite;
    filter: drop-shadow(0 0 4px rgba(123, 210, 236, 0.2));
  }

  .method-check.small {
    stroke-width: 3;
  }

  .method-check.method-guard-check {
    stroke: rgba(8, 12, 14, 0.84);
    stroke-width: 4;
  }

  .method-build-bar {
    stroke: rgba(123, 210, 236, 0.78);
    stroke-width: 4;
    stroke-dasharray: 22 54;
    animation: ${methodScan} 2.8s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.22));
  }

  .method-build-piece {
    fill: rgba(123, 210, 236, 0.13);
    stroke: rgba(205, 235, 243, 0.2);
    stroke-width: 1;
    animation: ${methodPulse} 3.3s ease-in-out infinite;
  }

  .piece-b {
    animation-delay: 0.38s;
  }

  .method-expansion-base,
  .method-expand-gate,
  .method-expansion-module .method-mini-node {
    fill: rgba(123, 210, 236, 0.07);
    stroke: rgba(205, 235, 243, 0.22);
    animation: ${methodPulse} 3.4s ease-in-out infinite;
  }

  .method-expand-gate {
    fill: rgba(123, 210, 236, 0.065);
    stroke: rgba(205, 235, 243, 0.24);
  }

  .method-expansion-flow {
    stroke: rgba(225, 245, 250, 0.86);
    stroke-width: 2.35;
    stroke-dasharray: 7 26;
    animation: ${methodResultFlow} 2.05s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.24));
  }

  .method-expansion-icon {
    stroke: rgba(205, 235, 243, 0.78);
    stroke-width: 1.7;
  }

  .method-integration-gear {
    stroke: rgba(205, 235, 243, 0.78);
    stroke-width: 1.9;
  }

  .method-integration-circuit {
    stroke: rgba(205, 235, 243, 0.66);
    stroke-width: 1.55;
    stroke-dasharray: 5 18;
    animation: ${methodResultFlow} 2.8s linear infinite;
  }

  .method-integration-pin {
    fill: rgba(205, 235, 243, 0.68);
    animation: ${methodPulse} 3.2s ease-in-out infinite;
  }

  .method-automation-gear-shape {
    fill: rgba(123, 210, 236, 0.15);
    stroke: rgba(205, 235, 243, 0.78);
    stroke-width: 1.35;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.2));
  }

  .method-automation-tooth {
    fill: rgba(123, 210, 236, 0.15);
    stroke: rgba(205, 235, 243, 0.78);
    stroke-width: 1.35;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.2));
  }

  .method-automation-rim {
    fill: none;
    stroke: rgba(205, 235, 243, 0.28);
    stroke-width: 1;
  }

  .method-automation-center {
    fill: rgba(12, 23, 26, 0.78);
    stroke: rgba(205, 235, 243, 0.56);
    stroke-width: 1.15;
  }

  .method-expansion-module.delay-a .method-mini-node,
  .method-expansion-module.delay-b .method-mini-node {
    animation-duration: 3.7s;
  }

  @media (prefers-reduced-motion: reduce) {
    .method-friction-ring,
    .method-orbit,
    .method-build-bar,
    .method-build-piece,
    .method-build-live-flow,
    .method-build-runner,
    .method-expansion-base,
    .method-expand-gate,
    .method-expansion-flow,
    .method-expansion-module .method-mini-node,
    .method-integration-circuit,
    .method-integration-pin,
    .method-workflow-step,
    .method-test-cursor,
    .method-selected-path,
    .method-choice-selected .method-candidate-card,
    .method-flow-card,
    .method-ai-core-spin,
    .method-search-result-flow,
    .method-gain-bar,
    .method-guard-shield {
      animation: none !important;
      opacity: 1;
    }
  }
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const MethodCard = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const MethodIndex = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.48);
`;

const MethodTitle = styled.h3`
  margin: 0;
  font-size: 26px;
  line-height: 1.08;
`;

const MethodText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.78);
`;

const NexBridgePanel = styled(GlassPanel)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 0.44fr);
  gap: clamp(22px, 4vw, 46px);
  align-items: center;
  padding: clamp(26px, 4vw, 42px);
  border-radius: 28px;
  border-color: rgba(123, 210, 236, 0.24);
  background:
    radial-gradient(circle at 12% 8%, rgba(123, 210, 236, 0.11), transparent 34%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.018)),
    rgba(5, 8, 10, 0.68);

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const NexBridgeCopy = styled.div`
  display: grid;
  gap: 13px;
`;

const NexBridgeTitle = styled.h2`
  margin: 0;
  max-width: 820px;
  font-size: clamp(30px, 4vw, 52px);
  line-height: 1;
  letter-spacing: -0.04em;
`;

const NexBridgeText = styled.p`
  margin: 0;
  max-width: 800px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 17px;
  line-height: 1.65;
`;

const NexBridgeButton = styled.button`
  all: unset;
  width: max-content;
  margin-top: 8px;
  cursor: pointer;
  color: rgba(214, 248, 255, 0.96);
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateX(2px);
    opacity: 0.9;
  }
`;

const NexBridgeSystem = styled.div`
  position: relative;
  min-height: clamp(240px, 25vw, 310px);
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 18px;
  overflow: hidden;
  isolation: isolate;
  border-radius: 28px;
  padding: clamp(20px, 3vw, 30px);
  border: 1px solid rgba(123, 210, 236, 0.18);
  background:
    radial-gradient(circle at 50% 42%, rgba(123, 210, 236, 0.18), transparent 38%),
    linear-gradient(145deg, rgba(123, 210, 236, 0.07), rgba(255, 255, 255, 0.018)),
    rgba(1, 7, 9, 0.54);

  &::before {
    content: "";
    position: absolute;
    inset: 18px;
    z-index: -2;
    border-radius: 22px;
    opacity: 0.5;
    background:
      linear-gradient(rgba(123, 210, 236, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(123, 210, 236, 0.08) 1px, transparent 1px);
    background-size: 46px 46px;
    mask-image: radial-gradient(circle at center, black 12%, transparent 72%);
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`;

const NexBridgeLogoStage = styled.div`
  position: relative;
  width: min(280px, 78%);
  min-height: 142px;
  display: grid;
  place-items: center;

  .nex-logo-glow {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 220px;
    aspect-ratio: 1;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(214, 248, 255, 0.28), rgba(123, 210, 236, 0.16) 34%, transparent 68%);
    animation: ${nexLogoGlow} 4.8s ease-in-out infinite;
    filter: blur(1px);
  }

  .nex-logo-glow-b {
    width: 295px;
    opacity: 0.28;
    animation-delay: -1.8s;
    background: radial-gradient(circle, rgba(123, 210, 236, 0.2), rgba(123, 210, 236, 0.08) 38%, transparent 70%);
  }
`;

const NexBridgeLogo = styled.img`
  position: relative;
  z-index: 2;
  display: block;
  width: min(220px, 86%);
  max-height: 96px;
  object-fit: contain;
  filter:
    drop-shadow(0 0 16px rgba(123, 210, 236, 0.28))
    drop-shadow(0 0 34px rgba(123, 210, 236, 0.12));
`;

const NexBridgeChipGrid = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, auto));
  gap: 8px;
  justify-content: center;

  span {
    min-width: 62px;
    padding: 8px 10px;
    border-radius: 999px;
    border: 1px solid rgba(123, 210, 236, 0.28);
    background: rgba(0, 0, 0, 0.32);
    color: rgba(214, 248, 255, 0.9);
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.13em;
    text-align: center;
    text-transform: uppercase;
    box-shadow: inset 0 0 18px rgba(123, 210, 236, 0.04);
  }

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, auto));
  }
`;

const ProofGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: clamp(24px, 3vw, 36px);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedProof = styled(GlassPanel)`
  appearance: none;
  display: grid;
  gap: 16px;
  padding: clamp(20px, 2.4vw, 24px);
  border-radius: 30px;
  text-align: left;
  color: #fff;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: ${({ $interactive }) => ($interactive ? "translateY(-2px)" : "none")};
    border-color: ${({ $interactive }) =>
      $interactive ? "rgba(255, 255, 255, 0.18)" : "rgba(255, 255, 255, 0.08)"};
  }
`;

const FeaturedMedia = styled(MarketingMediaSlot)`
  border-radius: 24px;
`;

const FeaturedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FeaturedLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
`;

const FeaturedTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
`;

const FeaturedTitle = styled.h3`
  margin: 0;
  font-size: clamp(32px, 4vw, 48px);
  line-height: 1;
`;

const FeaturedText = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.8);
`;

const FeaturedLink = styled.span`
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.92);
`;

const SupportingColumn = styled.div`
  display: grid;
  gap: 14px;
`;

const SupportingItem = styled(GlassPanel)`
  display: grid;
  gap: 8px;
  padding: 22px;
  border-radius: 24px;
`;

const SupportingTitle = styled.h4`
  margin: 0;
  font-size: 22px;
  line-height: 1.16;
`;

const SupportingText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.66;
  color: rgba(255, 255, 255, 0.76);
`;

const CtaPanel = styled(GlassPanel)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: clamp(24px, 3vw, 40px);
  align-items: end;
  padding: clamp(28px, 4vw, 40px);
  border-radius: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const CtaCopy = styled.div`
  display: grid;
  gap: 14px;
  max-width: 42rem;
`;

const CtaHeading = styled.h3`
  margin: 0;
  font-size: clamp(34px, 4vw, 50px);
  line-height: 1;
`;

const CtaText = styled.p`
  margin: 0;
  font-size: 18px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.8);
`;

const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 14px;
`;
